import { useMemo } from "react";
import { TargetSchema } from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getUnmappedCriticalSlots, type CriticalFieldAdvisoryContext } from "../../hooks/useCriticalFieldAdvisory";
import type { MappingValidationError } from "../../utils/fieldMappingValidation";
import type { UnifiedTarget } from "./TargetFieldSelect";

/** An upload-blocking mapping issue, source-agnostic. */
export interface BlockingItem {
  kind: "missing-required" | "duplicate" | "unknown-field";
  /** `schema:field` of the offending target field. */
  key: string;
  /** Human field label for the chip. */
  label: string;
  /** For duplicates and unknown fields: the source columns/labels mapped to this
   *  field. Both `resolveTargets` implementations navigate by these. */
  sourceHeaders?: string[];
}

interface UseMappingBlockersArgs {
  allSchemaKeys: string[];
  editedMappingsBySchema: Record<string, Record<string, string[]>>;
  /** Shared validator output — duplicates read `duplicate_target` from here. */
  validationErrors?: Record<string, MappingValidationError[]>;
  advisoryContext?: CriticalFieldAdvisoryContext;
  /** `schema:field` → option, for resolving human labels. */
  optionByKey: Map<string, UnifiedTarget>;
}

/**
 * Computes the upload-blocking mapping issues shared by every remap surface
 * (#6118). Missing-required fields come from the canonical
 * `getUnmappedCriticalSlots` (composite/conditional aware); duplicates come from
 * the shared `validateAllMappings` output — the same source of truth as the
 * Approve gate. Source-agnostic: the caller resolves each item to navigable
 * locations (spreadsheet columns, PDF regions, …).
 */
export const useMappingBlockers = ({
  allSchemaKeys,
  editedMappingsBySchema,
  validationErrors,
  advisoryContext = {},
  optionByKey,
}: UseMappingBlockersArgs): BlockingItem[] => {
  return useMemo(() => {
    const items: BlockingItem[] = [];

    // Duplicates — from the shared validator's `duplicate_target` errors.
    for (const [schema, errors] of Object.entries(validationErrors ?? {})) {
      for (const err of errors) {
        if (err.type !== "duplicate_target") continue;
        const key = `${schema}:${err.targetField}`;
        items.push({
          kind: "duplicate",
          key,
          label: optionByKey.get(key)?.label ?? err.targetField,
          sourceHeaders: err.sourceHeaders,
        });
      }
    }

    // Unknown / unresolvable target fields — from the shared validator's
    // `unknown_field` errors (#6425). These already block Approve & Load, but
    // were never surfaced here, so the analyst saw a block with no diagnosable
    // reason. Unlike `duplicate_target` (aggregated by field), the validator
    // emits `unknown_field` once per source header, so group by key first —
    // otherwise N headers → N chips sharing one React key, and the field listed
    // N times. Merging headers also lets resolveTargets cycle through every
    // offending column, exactly like the duplicate case. The raw field name is
    // the label on purpose: it is NOT a valid field, so a title-cased label
    // would hide what's wrong.
    const unknownByKey = new Map<string, { field: string; headers: Set<string> }>();
    for (const [schema, errors] of Object.entries(validationErrors ?? {})) {
      for (const err of errors) {
        if (err.type !== "unknown_field") continue;
        const key = `${schema}:${err.targetField}`;
        const acc = unknownByKey.get(key) ?? { field: err.targetField, headers: new Set<string>() };
        // Set dedupes: one header can carry the same unknown field twice (the
        // multiple-select doesn't prevent it), which would otherwise list the
        // same column twice and read as "(2)".
        for (const h of err.sourceHeaders) acc.headers.add(h);
        unknownByKey.set(key, acc);
      }
    }
    for (const [key, { field, headers }] of unknownByKey) {
      items.push({ kind: "unknown-field", key, label: field, sourceHeaders: [...headers] });
    }

    // Missing required fields — canonical resolver (composite alternatives
    // collapse; conditional requirements filtered against the document).
    for (const schema of allSchemaKeys) {
      const mappedFields = Object.values(editedMappingsBySchema[schema] ?? {}).flat();
      for (const entry of getUnmappedCriticalSlots(schema as TargetSchema, mappedFields, advisoryContext)) {
        if (entry.severity !== "required") continue;
        const key = `${schema}:${entry.field}`;
        items.push({
          kind: "missing-required",
          key,
          label: optionByKey.get(key)?.label ?? formatToTitleCase(entry.field),
        });
      }
    }
    return items;
  }, [validationErrors, allSchemaKeys, editedMappingsBySchema, advisoryContext, optionByKey]);
};

// ── Source-match ranking ─────────────────────────────────────────────────────

const tokenize = (s: string) =>
  s
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/**
 * Ranks source labels (spreadsheet headers or PDF field labels) by token
 * overlap with a target field's label/name — powers "jump to the source that
 * most likely holds this field" and the Suggested cue. Returns source indices,
 * best match first; empty when nothing plausibly matches.
 */
export const rankSourcesByFieldMatch = (fieldLabel: string, fieldName: string, sources: string[]): number[] => {
  const fieldTokens = new Set([...tokenize(fieldLabel), ...tokenize(fieldName)]);
  if (fieldTokens.size === 0) return [];
  const scored: { i: number; overlap: number }[] = [];
  sources.forEach((s, i) => {
    let overlap = 0;
    for (const t of tokenize(s)) if (fieldTokens.has(t)) overlap += 1;
    if (overlap >= 1) scored.push({ i, overlap });
  });
  scored.sort((a, b) => b.overlap - a.overlap || a.i - b.i);
  return scored.map((s) => s.i);
};
